import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import TireTable from './tire-table';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import { getAllTireBrands, getAllTirePositions, getAllTireSeasons, getAllTireSizes, getAllTireStatuses, getAllTypeTires } from '../../services/tires-service';
import { AccountsContext } from '../../components/context/account-context';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { getAllVendorsMinimal } from '../../services/vendors-service';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import { CSVLink } from 'react-csv';
import logo from '../../assets/images/logo.jpg';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Tires = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [tireData, setTireData] = useState([]);
    const [error, setError] = useState(null);
    const [brands, setBrands] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [positions, setPositions] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingGlobalData, setLoadingGlobalData] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchGlobalData = async () => {
        if (!accountId) return;
        setLoadingGlobalData(true);

        try {
            const [bransRes, sizeRes, seasonRes, positionRes, statusOptionsRes, vehiclesRes, vendorRes] = await Promise.all([
                getAllTireBrands(accountId),
                getAllTireSizes(accountId),
                getAllTireSeasons(accountId),
                getAllTirePositions(accountId),
                getAllTireStatuses(accountId),
                getVehiclesMinimal(accountId),
                getAllVendorsMinimal(accountId),
            ]);

            if (bransRes.success) setBrands(bransRes.data.data || []);
            if (sizeRes.success) setSizes(sizeRes.data.data || []);
            if (seasonRes.success) setSeasons(seasonRes.data.data || []);
            if (positionRes.success) setPositions(positionRes.data.data || []);
            if (statusOptionsRes.success) setStatusOptions(statusOptionsRes.data.data || []);
            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            if (vendorRes.success) setVendors(vendorRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setLoadingGlobalData(false);
        }
    };

    const fetchTires = async () => {
        setLoading(true);
        try {
            // const { from, to } = getDateRangeUTC(dateRange);
            const response = await getAllTypeTires(accountId);
            console.log(response)
            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
                setTireData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch meter records');
            }
        } catch (err) {
            setError('An error occurred while fetching meter records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTires();
    }, [accountId]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchTires();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? tireData.filter((_, index) => selectedRows[index])
        : tireData;

    // CSV Data preparation
    const csvData = exportData.map(item => ({
        'Tire ID': item.tireId || '----',
        Brand: item.brand ? camelCaseToWords(item.brand) : '----',
        Model: item.model || '----',
        Vendor: item.vendor?.name ? camelCaseToWords(item.vendor.name) : '----',
        TIN: item.tin || '----',
        Size: item.size || '----',
        Season: item.season ? camelCaseToWords(item.season) : '----',
        Rim: item.rim || '----',
        Vehicle: item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
        Position: item.position ? camelCaseToWords(item.position) : '----',
        Status: item.status ? camelCaseToWords(item.status) : '----',
    }));

    // PDF Export function
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;
        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Tire Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Table
        autoTable(doc, {
            head: [['Tire ID', 'Brand', 'Model', 'Vendor', 'TIN', 'Size', 'Season', 'Rim', 'Vehicle', 'Position', 'Status']],
            body: exportData.map(item => [
                item.tireId || '----',
                item.brand ? camelCaseToWords(item.brand) : '----',
                item.model || '----',
                item.vendor?.name ? camelCaseToWords(item.vendor.name) : '----',
                item.tin || '----',
                item.size || '----',
                item.season ? camelCaseToWords(item.season) : '----',
                item.rim || '----',
                item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
                item.position ? camelCaseToWords(item.position) : '----',
                item.status ? camelCaseToWords(item.status) : '----',
            ]),
            startY: titleY + 5,
            styles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 20 },
                1: { cellWidth: 20 },
                2: { cellWidth: 20 },
                3: { cellWidth: 20 },
                4: { cellWidth: 20 },
                5: { cellWidth: 20 },
                6: { cellWidth: 15 },
                7: { cellWidth: 15 },
                8: { cellWidth: 20 },
                9: { cellWidth: 15 },
                10: { cellWidth: 15 },
            },
        });

        // Save with a simple filename
        const fileName = `tires-report.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4 ">

            <div className='flex mb-2 items-center justify-between'>
                <div className=''>
                    <Heading>Tires</Heading>
                </div>
                <Button
                    label='Add Tire'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4' >

                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* <DateRangePicker onChange={setDateRange} /> */}

                    </div>

                    {/* Export buttons - added here */}
                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`Tires-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>

                    </div>
                </div>

                {/* Table */}
                <TireTable
                    data={tireData}
                    setData={setTireData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchTires={fetchTires}
                    loadingData={loading}
                    accountId={accountId}
                    brands={brands}
                    positions={positions}
                    sizes={sizes}
                    vehicles={vehicles}
                    vendors={vendors}
                    seasons={seasons}
                    statusOptions={statusOptions}
                    fetchGlobalData={fetchGlobalData}
                    loadingGlobalData={loadingGlobalData}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        brands={brands}
                        positions={positions}
                        sizes={sizes}
                        accountId={accountId}
                        vehicles={vehicles}
                        vendors={vendors}
                        seasons={seasons}
                        fetchGlobalData={fetchGlobalData}
                        loadingGlobalData={loadingGlobalData}
                    />
                </Modal>

            </div>
        </div >
    );
};

export default Tires;